import { useEffect, useState } from "react";
import { formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2 } from "lucide-react";

const EMPTY_ITEM = { description: "", quantity: 1, unit_price: 0 };

export default function QuotationForm({ open, onOpenChange, customers, initial, saving, onSubmit, title }) {
  const [customerId, setCustomerId] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [taxPercent, setTaxPercent] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [currency, setCurrency] = useState("USD");
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [changeNote, setChangeNote] = useState("");

  useEffect(() => {
    if (open) {
      setCustomerId(initial?.customer_id || "");
      setItems(initial?.items?.length ? initial.items.map((i) => ({ ...i })) : [{ ...EMPTY_ITEM }]);
      setTaxPercent(initial?.tax_percent ?? 0);
      setDiscountPercent(initial?.discount_percent ?? 0);
      setCurrency(initial?.currency || "USD");
      setValidUntil(initial?.valid_until || "");
      setNotes(initial?.notes || "");
      setChangeNote("");
    }
  }, [open, initial]);

  const setItem = (idx, key, value) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    setItems(next);
  };

  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const discount = (subtotal * Number(discountPercent || 0)) / 100;
  const tax = ((subtotal - discount) * Number(taxPercent || 0)) / 100;
  const total = subtotal - discount + tax;

  const valid = customerId && items.every((i) => i.description.trim().length >= 2 && Number(i.quantity) > 0);

  const submit = () => {
    const payload = {
      items: items.map((i) => ({ description: i.description.trim(), quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
      tax_percent: Number(taxPercent) || 0,
      discount_percent: Number(discountPercent) || 0,
      currency,
      valid_until: validUntil,
      notes,
    };
    if (initial) payload.change_note = changeNote;
    else payload.customer_id = customerId;
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="quotation-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!initial && (
            <div className="space-y-1.5">
              <Label>Customer *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="quotation-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`quotation-customer-option-${c.id}`}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Line Items *</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="quotation-add-item-button">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 items-end gap-2 rounded-md border border-border p-2.5" data-testid={`quotation-item-row-${idx}`}>
                  <div className="col-span-12 sm:col-span-6">
                    <Label className="text-xs text-muted-foreground">Service Description</Label>
                    <Input value={item.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="e.g. Calibration of Pressure Gauge 0-10 bar" data-testid={`quotation-item-description-${idx}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">Qty</Label>
                    <Input type="number" min="0.5" step="0.5" value={item.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} data-testid={`quotation-item-quantity-${idx}`} />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Label className="text-xs text-muted-foreground">Unit Price</Label>
                    <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} data-testid={`quotation-item-price-${idx}`} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Button type="button" variant="ghost" size="icon" disabled={items.length === 1} onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`quotation-item-remove-${idx}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Tax %</Label>
              <Input type="number" min="0" max="100" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} data-testid="quotation-tax-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Discount %</Label>
              <Input type="number" min="0" max="100" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} data-testid="quotation-discount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="quotation-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["USD", "EUR", "INR", "GBP", "IDR"].map((c) => (
                    <SelectItem key={c} value={c}>{c === "IDR" ? "IDR (Rupiah)" : c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Valid Until</Label>
              <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quotation-valid-until-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes / Terms</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quotation-notes-input" />
          </div>

          {initial && (
            <div className="space-y-1.5">
              <Label>Revision Note (recorded in version history)</Label>
              <Input value={changeNote} onChange={(e) => setChangeNote(e.target.value)} placeholder="e.g. Updated pricing per customer call" data-testid="quotation-change-note-input" />
            </div>
          )}

          <div className="rounded-md border border-border bg-muted/40 px-4 py-3 font-mono text-sm" data-testid="quotation-totals-preview">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatMoney(subtotal, currency)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Discount ({discountPercent || 0}%)</span><span>-{formatMoney(discount, currency)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax ({taxPercent || 0}%)</span><span>{formatMoney(tax, currency)}</span></div>
            <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>Total</span><span data-testid="quotation-total-preview">{formatMoney(total, currency)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quotation-form-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={saving || !valid} data-testid="quotation-form-save-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {initial ? "Save as New Revision" : "Create Quotation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
